/**
 * Combobox Component Styles
 *
 * Type-safe styling variants using Class Variance Authority (CVA).
 *
 * @see docs/planning/combobox-priority-1-component-architecture.md
 */

import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Trigger button styles
 */
export const comboboxTriggerStyles = cva(
  [
    'inline-flex items-center justify-between',
    'w-full rounded-md border',
    'bg-white text-gray-900',
    'transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      size: {
        sm: 'h-8 px-2 text-xs gap-1',
        md: 'h-10 px-3 text-sm gap-2',
        lg: 'h-12 px-4 text-base gap-3',
      },
      variant: {
        outline: [
          'border-gray-300',
          'hover:border-gray-400',
          'focus:ring-blue-500 focus:border-blue-500',
        ],
        filled: ['border-transparent bg-gray-100', 'hover:bg-gray-200', 'focus:ring-blue-500'],
        flushed: [
          'border-0 border-b-2 border-gray-300',
          'rounded-none px-0',
          'hover:border-gray-400',
          'focus:ring-0 focus:border-blue-500',
        ],
        unstyled: ['border-0 bg-transparent shadow-none', 'focus:ring-0'],
      },
      error: {
        true: ['border-red-500', 'focus:ring-red-500 focus:border-red-500'],
        false: '',
      },
      disabled: {
        true: 'bg-gray-100',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'outline',
      error: false,
      disabled: false,
    },
  }
);

/**
 * Content dropdown styles
 */
export const comboboxContentStyles = cva(
  ['rounded-md border border-gray-200', 'bg-white shadow-lg', 'overflow-hidden', 'z-50'],
  {
    variants: {
      size: {
        sm: 'min-w-[200px] max-w-[300px]',
        md: 'min-w-[250px] max-w-[400px]',
        lg: 'min-w-[300px] max-w-[500px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * Search input styles
 */
export const comboboxSearchStyles = cva(
  ['relative flex items-center', 'border-b border-gray-200', 'bg-white'],
  {
    variants: {
      size: {
        sm: 'h-8 px-2',
        md: 'h-10 px-3',
        lg: 'h-12 px-4',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * Virtual list container styles
 */
export const comboboxVirtualListStyles = cva(['overflow-auto', 'overscroll-contain'], {
  variants: {
    size: {
      sm: 'max-h-[200px]',
      md: 'max-h-[300px]',
      lg: 'max-h-[400px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

/**
 * List item styles
 */
export const comboboxItemStyles = cva(
  [
    'flex items-center gap-2',
    'w-full px-3 cursor-pointer',
    'transition-colors duration-150',
    'select-none',
  ],
  {
    variants: {
      size: {
        sm: 'h-8 text-xs',
        md: 'h-10 text-sm',
        lg: 'h-12 text-base',
      },
      selected: {
        true: 'bg-blue-50 text-blue-900 font-medium',
        false: 'text-gray-900',
      },
      focused: {
        true: 'bg-gray-100',
        false: '',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: 'hover:bg-gray-50',
      },
    },
    compoundVariants: [
      {
        selected: true,
        focused: true,
        className: 'bg-blue-100',
      },
    ],
    defaultVariants: {
      size: 'md',
      selected: false,
      focused: false,
      disabled: false,
    },
  }
);

/**
 * Multi-value badges container
 */
export const comboboxMultiValueStyles = cva(['flex flex-wrap gap-1', 'min-h-0'], {
  variants: {
    size: {
      sm: 'gap-0.5',
      md: 'gap-1',
      lg: 'gap-1.5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

/**
 * Badge styles for multi-select
 */
export const comboboxBadgeStyles = cva(
  [
    'inline-flex items-center gap-1',
    'px-2 py-0.5 rounded',
    'text-xs font-medium',
    'transition-colors',
  ],
  {
    variants: {
      colorPalette: {
        blue: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
        green: 'bg-green-100 text-green-800 hover:bg-green-200',
        red: 'bg-red-100 text-red-800 hover:bg-red-200',
        yellow: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
        purple: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
        gray: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: 'cursor-pointer',
      },
    },
    defaultVariants: {
      colorPalette: 'blue',
      disabled: false,
    },
  }
);

/**
 * Empty state styles
 */
export const comboboxEmptyStyles = cva(
  ['flex flex-col items-center justify-center', 'text-gray-500 text-center'],
  {
    variants: {
      size: {
        sm: 'py-4 px-3 text-xs',
        md: 'py-6 px-4 text-sm',
        lg: 'py-8 px-5 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * Loading state styles
 */
export const comboboxLoadingStyles = cva(
  ['flex items-center justify-center gap-2', 'text-gray-600'],
  {
    variants: {
      size: {
        sm: 'py-4 px-3 text-xs',
        md: 'py-6 px-4 text-sm',
        lg: 'py-8 px-5 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/**
 * Spinner styles
 */
export const comboboxSpinnerStyles = cva(
  ['animate-spin rounded-full border-2 border-gray-300', 'border-t-blue-500'],
  {
    variants: {
      size: {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

// Export variant props types
export type ComboboxTriggerVariants = VariantProps<typeof comboboxTriggerStyles>;
export type ComboboxContentVariants = VariantProps<typeof comboboxContentStyles>;
export type ComboboxSearchVariants = VariantProps<typeof comboboxSearchStyles>;
export type ComboboxVirtualListVariants = VariantProps<typeof comboboxVirtualListStyles>;
export type ComboboxItemVariants = VariantProps<typeof comboboxItemStyles>;
export type ComboboxMultiValueVariants = VariantProps<typeof comboboxMultiValueStyles>;
export type ComboboxBadgeVariants = VariantProps<typeof comboboxBadgeStyles>;
export type ComboboxEmptyVariants = VariantProps<typeof comboboxEmptyStyles>;
export type ComboboxLoadingVariants = VariantProps<typeof comboboxLoadingStyles>;
export type ComboboxSpinnerVariants = VariantProps<typeof comboboxSpinnerStyles>;
